import {
  CondicaoCampo,
  GRUPO_TOPO,
  ItemFormulario,
  RespostaFormulario,
  SchemaFormulario,
  ValorResposta,
} from '@/types/formulario'

/**
 * Lógica de condição do formulário dinâmico — CÓPIA DE UX da regra do backend
 * (`ValidadorFormulario`, que é a autoridade). Serve só para mostrar/ocultar campos, pôr o
 * asterisco e não mandar resposta de campo oculto; nunca decide se a reserva pode seguir.
 *
 * As duas implementações leem a MESMA fixture
 * (`originei-oportunidades/src/test/resources/formularios/casos-condicao.json`) — risco R1
 * do AN-6. Semântica fixada lá:
 *  - campo sem resposta (null, "" ou só espaços) nunca satisfaz condição, nem `DIFERENTE`;
 *  - comparação textual exata após `trim` (SIM_NAO trafega como `true`/`false`);
 *    se os dois lados são numéricos, compara como número ("2" = "2.0");
 *  - `UM_DE` usa `valores` (ou `[valor]`); `MAIOR_QUE` exige os dois lados numéricos.
 */

/** Texto canônico de um valor de resposta; `null` quando não há resposta. */
export const valorComoTexto = (v: ValorResposta | undefined): string | null => {
  if (v == null) return null
  const t = String(v).trim()
  return t === '' ? null : t
}

const comoNumero = (t: string | null | undefined): number | null => {
  if (t == null || t.trim() === '') return null
  const n = Number(t.trim())
  return Number.isFinite(n) ? n : null
}

const iguais = (a: string, b: string): boolean => {
  const na = comoNumero(a)
  const nb = comoNumero(b)
  if (na != null && nb != null) return na === nb
  return a === b.trim()
}

/** Avalia uma condição contra o valor do campo referenciado. Sem condição = verdadeira. */
export const avaliarCondicao = (
  condicao: CondicaoCampo | null | undefined,
  valorDoCampo: (chave: string) => ValorResposta | undefined
): boolean => {
  if (!condicao) return true
  const atual = valorComoTexto(valorDoCampo(condicao.campo))
  if (atual == null) return false
  switch (condicao.operador) {
    case 'PREENCHIDO':
      return true
    case 'IGUAL':
      return condicao.valor != null && iguais(atual, String(condicao.valor))
    case 'DIFERENTE':
      return condicao.valor == null || !iguais(atual, String(condicao.valor))
    case 'UM_DE': {
      const lista = condicao.valores ?? (condicao.valor != null ? [condicao.valor] : [])
      return lista.some((v) => iguais(atual, String(v)))
    }
    case 'MAIOR_QUE': {
      const n = comoNumero(atual)
      const limite = comoNumero(condicao.valor ?? null)
      return n != null && limite != null && n > limite
    }
    default:
      // Operador desconhecido: mostra o campo (o servidor é a autoridade).
      return true
  }
}

// ─── Respostas ──────────────────────────────────────────────────────────────

const chaveResposta = (grupo: string, indice: number, campo: string) => `${grupo}\u0000${indice}\u0000${campo}`

/** Índice das respostas por (grupo, índice, campo). */
export const indexarRespostas = (
  respostas: RespostaFormulario[]
): Map<string, ValorResposta> => {
  const m = new Map<string, ValorResposta>()
  for (const r of respostas) m.set(chaveResposta(r.grupo ?? GRUPO_TOPO, r.indice ?? 0, r.campo), r.valor)
  return m
}

export const valorResposta = (
  indice: Map<string, ValorResposta>,
  grupo: string,
  posicao: number,
  campo: string
): ValorResposta | undefined => indice.get(chaveResposta(grupo, posicao, campo))

/** Resolve a chave de uma condição: irmão do grupo (mesmo item) primeiro, depois o topo. */
export const resolvedor = (
  indice: Map<string, ValorResposta>,
  grupo?: ItemFormulario,
  posicao = 0
) => (chave: string): ValorResposta | undefined => {
  if (grupo && (grupo.campos ?? []).some((c) => c.chave === chave)) {
    return valorResposta(indice, grupo.chave, posicao, chave)
  }
  return valorResposta(indice, GRUPO_TOPO, 0, chave)
}

export const itemVisivel = (
  item: ItemFormulario,
  resolver: (chave: string) => ValorResposta | undefined
): boolean => avaliarCondicao(item.condition, resolver)

/** Mostra asterisco? Tem marco e, se houver `requiredWhen`, ele é verdadeiro. */
export const itemObrigatorio = (
  item: ItemFormulario,
  resolver: (chave: string) => ValorResposta | undefined
): boolean => !!item.obrigatorioEm && (!item.requiredWhen || avaliarCondicao(item.requiredWhen, resolver))

/** Teto de itens de um grupo quando o schema não diz (mesmo teto do exemplo do AN-6). */
export const TETO_ITENS_GRUPO = 20

/**
 * Quantos itens (cartões) o grupo mostra. Com `quantidadeDe`: o valor do NUMERO indicado,
 * limitado a [0, maxItems]. Sem ele: os itens já respondidos, no mínimo `minItems` (ou 1).
 */
export const quantidadeItensGrupo = (
  grupo: ItemFormulario,
  respostas: RespostaFormulario[]
): number => {
  const teto = grupo.maxItems ?? TETO_ITENS_GRUPO
  if (grupo.quantidadeDe) {
    const indice = indexarRespostas(respostas)
    const n = comoNumero(valorComoTexto(valorResposta(indice, GRUPO_TOPO, 0, grupo.quantidadeDe)))
    if (n == null || n < 0) return 0
    return Math.min(Math.floor(n), teto)
  }
  if (!grupo.repeatableGroup) return 1
  const respondidos = respostas
    .filter((r) => r.grupo === grupo.chave)
    .reduce((max, r) => Math.max(max, (r.indice ?? 0) + 1), 0)
  return Math.min(Math.max(respondidos, grupo.minItems ?? 1), teto)
}

/**
 * O que vai no PUT: tira resposta vazia, de campo oculto por condição, de chave que o
 * schema não conhece e de item de grupo além da quantidade. O servidor faz o mesmo
 * descarte (é a autoridade); aqui é para não mandar lixo nem dado sensível à toa.
 */
export const respostasParaEnvio = (
  schema: SchemaFormulario,
  respostas: RespostaFormulario[]
): RespostaFormulario[] => {
  const indice = indexarRespostas(respostas)
  const topo = resolvedor(indice)
  const saida: RespostaFormulario[] = []
  for (const item of schema.itens ?? []) {
    if (!itemVisivel(item, topo)) continue
    if (item.tipo === 'GRUPO') {
      const n = quantidadeItensGrupo(item, respostas)
      for (let i = 0; i < n; i++) {
        const irmao = resolvedor(indice, item, i)
        for (const campo of item.campos ?? []) {
          if (!itemVisivel(campo, irmao)) continue
          const v = valorResposta(indice, item.chave, i, campo.chave)
          if (valorComoTexto(v) != null) saida.push({ grupo: item.chave, indice: i, campo: campo.chave, valor: v ?? null })
        }
      }
      continue
    }
    const v = valorResposta(indice, GRUPO_TOPO, 0, item.chave)
    if (valorComoTexto(v) != null) saida.push({ grupo: GRUPO_TOPO, indice: 0, campo: item.chave, valor: v ?? null })
  }
  return saida
}

/** Troca (ou apaga, com valor vazio) uma resposta, devolvendo a lista nova. */
export const comResposta = (
  respostas: RespostaFormulario[],
  grupo: string,
  indice: number,
  campo: string,
  valor: ValorResposta
): RespostaFormulario[] => {
  const sem = respostas.filter(
    (r) => !((r.grupo ?? GRUPO_TOPO) === grupo && (r.indice ?? 0) === indice && r.campo === campo)
  )
  return valorComoTexto(valor) == null ? sem : [...sem, { grupo, indice, campo, valor }]
}

/** Remove o item `indice` de um grupo livre e renumera os seguintes (sem buraco). */
export const semItemDoGrupo = (
  respostas: RespostaFormulario[],
  grupo: string,
  indice: number
): RespostaFormulario[] =>
  respostas
    .filter((r) => !(r.grupo === grupo && r.indice === indice))
    .map((r) => (r.grupo === grupo && r.indice > indice ? { ...r, indice: r.indice - 1 } : r))
